import type { ValidationErrorDetail } from "./types";

// ============================================================================
// CUSTOM ERROR CLASSES
// ============================================================================

export class CommandError extends Error {
  name = "CommandError";
  constructor(
    message: string,
    public command?: string,
  ) {
    super(message);
  }
}

/**
 * Error thrown when command input validation fails
 * Contains detailed information about which fields failed and why
 */
export class InputValidationError extends CommandError {
  name = "InputValidationError";

  constructor(
    command: string,
    public errors: ValidationErrorDetail[],
    public input?: unknown,
  ) {
    const fieldList = errors.map((e) => e.path).join(", ");
    super(`Input validation failed for ${command}: [${fieldList}]`, command);
  }

  /**
   * Get errors for a specific field path
   */
  getFieldErrors(path: string): ValidationErrorDetail[] {
    return this.errors.filter((e) => e.path === path);
  }

  /**
   * Get all required field errors
   */
  getRequiredErrors(): ValidationErrorDetail[] {
    return this.errors.filter((e) => e.code === "required");
  }

  /**
   * Get missing required field paths
   */
  getMissingFields(): string[] {
    return this.getRequiredErrors().map((e) => e.path);
  }

  /**
   * Check if a specific field has errors
   */
  hasFieldError(path: string): boolean {
    return this.errors.some((e) => e.path === path);
  }

  /**
   * Convert to a simple object for serialization
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      command: this.command,
      errors: this.errors,
    };
  }
}

export class CommandNotFoundError extends CommandError {
  name = "CommandNotFoundError";
  constructor(command: string) {
    super(`Command not found: ${command}`, command);
  }
}

export class CommandUnavailableError extends CommandError {
  name = "CommandUnavailableError";
  constructor(command: string) {
    super(`Command not available: ${command}`, command);
  }
}

export class CommandTimeoutError extends CommandError {
  name = "CommandTimeoutError";
  constructor(command: string, timeout: number) {
    super(`Command timeout: ${command} (${timeout}ms)`, command);
  }
}

export class CommandExecutionError extends CommandError {
  name = "CommandExecutionError";
  cause: Error;

  constructor(command: string, originalError: Error) {
    super(
      `Command execution failed: ${command} - ${originalError.message}`,
      command,
    );
    this.cause = originalError;
  }
}

// ============================================================================
// ERROR FACTORY
// ============================================================================

export function createCommandError(
  type: string,
  command: string,
  details?: any,
): CommandError {
  switch (type) {
    case "not-found":
      return new CommandNotFoundError(command);
    case "unavailable":
      return new CommandUnavailableError(command);
    case "timeout":
      return new CommandTimeoutError(command, details?.timeout || 0);
    case "execution":
      return new CommandExecutionError(
        command,
        details?.error || new Error("Unknown error"),
      );
    case "validation":
      return new InputValidationError(
        command,
        details?.errors || [],
        details?.input,
      );
    default:
      return new CommandError(`Unknown error type: ${type}`, command);
  }
}

// ============================================================================
// TYPE GUARDS
// ============================================================================

export function isInputValidationError(
  error: unknown,
): error is InputValidationError {
  return error instanceof InputValidationError;
}

export function isCommandError(error: unknown): error is CommandError {
  return error instanceof CommandError;
}
